// FILE: ProfileSection.logic.ts
// Purpose: Decide whether a picked file may be uploaded as an avatar, and say why not in words a user can act on.
// Exports: `AVATAR_FILE_INPUT_ACCEPT`, `validateAvatarFile`, `formatBytes`.

import { AVATAR_ACCEPTED_MIME_TYPES, AVATAR_MAX_BYTES } from "@taskin-code/contracts";

/** `accept` attribute for the file input, built from the one accepted list. */
export const AVATAR_FILE_INPUT_ACCEPT = AVATAR_ACCEPTED_MIME_TYPES.join(",");

export type AvatarFileRejection =
  | { readonly ok: true }
  | { readonly ok: false; readonly message: string };

/**
 * Rounds *up*, which matters more than it looks: this formats both the file
 * and the limit in the same sentence, and rounding to nearest turns a file one
 * byte over into "That image is 5 MB. The limit is 5 MB." Rounding up keeps
 * the two numbers on the right sides of each other.
 */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    const megabytes = bytes / (1024 * 1024);
    // One decimal only below 10 MB — "0.4 MB" is useful, "5.0 MB" is noise.
    return `${megabytes >= 10 ? Math.ceil(megabytes) : Math.ceil(megabytes * 10) / 10} MB`;
  }
  return `${Math.max(1, Math.ceil(bytes / 1024))} KB`;
}

/**
 * Client-side gate on a picked file.
 *
 * Not a security check — the server sniffs the bytes and enforces the same
 * ceiling, and it has to, because nothing stops a caller from skipping this
 * function entirely. What this buys is the user's time: base64-encoding a
 * 40 MB photo and uploading it only to be told "too large" is several seconds
 * of nothing happening, and the file input's `accept` filter is a suggestion
 * that a drag-and-drop or a "show all files" picker walks straight past.
 *
 * The type is checked against the declared MIME type, which a file can lie
 * about. That is fine here for the same reason: this is a courtesy, and the
 * server is what decides.
 */
export function validateAvatarFile(file: {
  readonly type: string;
  readonly size: number;
}): AvatarFileRejection {
  const type = file.type.toLowerCase();
  if (!(AVATAR_ACCEPTED_MIME_TYPES as ReadonlyArray<string>).includes(type)) {
    return {
      ok: false,
      message: "Choose a PNG, JPEG, GIF or WebP image.",
    };
  }
  if (file.size > AVATAR_MAX_BYTES) {
    return {
      ok: false,
      message: `That image is ${formatBytes(file.size)}. The limit is ${formatBytes(AVATAR_MAX_BYTES)}.`,
    };
  }
  if (file.size === 0) {
    return { ok: false, message: "That file is empty." };
  }
  return { ok: true };
}
